import { Environment, Network, RecordSource, RequestParameters, Store, UploadableMap, Variables } from 'relay-runtime';
import { getCookie, setCookie } from 'src/shared/helpers';
import { GRAPHQL_URL } from './fetchQuery';
import { getHeaders, getRequestBody, handleData, isMutation } from './helpers';

/**
 * Relay environment dedicated to the DAM-Projects subgraph.
 *
 * It points to the same Apollo Router endpoint as the main environment (GRAPHQL_URL),
 * but authenticates against DAM-Projects with the two headers it expects:
 *   - `jwt`: the DAM access token, read from the `dam_access_token` cookie
 *   - `tenant_id`: the DAM tenant id, read from the `dam_tenant_id` cookie
 *
 * These cookies are populated by calling the `damSession` resolver on the main
 * GraphQL endpoint (see `refreshDamSession`), so the Backoffice no longer depends
 * on the cookies set by the DAM BFF iframe. They are stored client-readable (not
 * httpOnly, `path=/`) so this environment can read them.
 *
 * This is kept separate from the main environment (`./environment`) so the standard
 * Backoffice traffic is never affected.
 */

export const DAM_ACCESS_TOKEN_COOKIE = 'dam_access_token';
export const DAM_TENANT_ID_COOKIE = 'dam_tenant_id';

const DAM_SESSION_COOKIE_DAYS = 1;

interface DamSession {
    accessToken: string;
    tenantId: string;
}

interface DamUserRoleMap {
    tenantId: string;
    roles: string[];
}

interface DamUserMetadataEntry {
    key: string;
    value: string;
}

export interface DamUserInfo {
    id: string;
    email: string;
    username: string;
    profile: {
        givenName: string;
        familyName: string;
        middleName: string;
        displayName: string;
        preferredLanguage: string;
    };
    roles: DamUserRoleMap[];
    metadata: DamUserMetadataEntry[];
}

const DAM_SESSION_QUERY = `query DamSessionQuery {
  damSession {
    accessToken
    tenantId
  }
}`;

const DAM_USER_INFO_QUERY = `query DamUserInfoQuery($token: String!) {
  damUserInfo(token: $token) {
    id
    email
    username
    profile {
      givenName
      familyName
      middleName
      displayName
      preferredLanguage
    }
    roles {
      tenantId
      roles
    }
    metadata {
      key
      value
    }
  }
}`;

export const hasDamSessionCookies = (): boolean => !!getCookie(DAM_ACCESS_TOKEN_COOKIE) && !!getCookie(DAM_TENANT_ID_COOKIE);

// Shared in-flight refresh promise — prevents multiple concurrent calls from
// each triggering a separate login when the session expires simultaneously.
let _refreshPromise: Promise<DamSession> | null = null;

let _damUserInfo: DamUserInfo | null = null;

export const getDamUserInfo = (): DamUserInfo | null => _damUserInfo;

/**
 * Calls `damSession` on the main GraphQL endpoint and persists the returned
 * access token / tenant id as client-readable cookies (`path=/`).
 * Concurrent calls share the same in-flight request.
 */
export const refreshDamSession = (): Promise<DamSession> => {
    if (_refreshPromise) {
        return _refreshPromise;
    }

    _refreshPromise = (async (): Promise<DamSession> => {
    const response = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: getHeaders() as unknown as Headers,
        body: JSON.stringify({ query: DAM_SESSION_QUERY })
    });

    const data = await response.json();
    const session = data?.data?.damSession as DamSession | undefined;

    if (!session?.accessToken || !session?.tenantId) {
        throw new Error('Unable to retrieve DAM session');
    }

        setCookie(DAM_ACCESS_TOKEN_COOKIE, session.accessToken, DAM_SESSION_COOKIE_DAYS);
        setCookie(DAM_TENANT_ID_COOKIE, session.tenantId, DAM_SESSION_COOKIE_DAYS);

        return session;
    })().finally(() => {
        _refreshPromise = null;
    });

    return _refreshPromise;
};

/**
 * Ensures the DAM session cookies are present, fetching a fresh session when
 * they are missing. Never throws: failures are logged so the caller can still
 * proceed and let this environment surface the error if needed.
 */
export const ensureDamSession = async (): Promise<void> => {
    if (!hasDamSessionCookies()) {
        try {
            await refreshDamSession();
        } catch (err) {
            // eslint-disable-next-line
            console.error('ensureDamSession err: ', err);
        }
    }
    await ensureDamUserInfo();
};

/**
 * Fetches DAM user info for the current session token and stores it in the
 * module-level _damUserInfo variable. Always fetches — no caching check.
 * Never throws: failures are logged and _damUserInfo remains null.
 */
export const ensureDamUserInfo = async (): Promise<void> => {
    const token = getCookie(DAM_ACCESS_TOKEN_COOKIE);
    if (!token) {
        return;
    }

    try {
        const response = await fetch(GRAPHQL_URL, {
            method: 'POST',
            headers: getHeaders() as unknown as Headers,
            body: JSON.stringify({ query: DAM_USER_INFO_QUERY, variables: { token } })
        });

        const data = await response.json();
        const userInfo = data?.data?.damUserInfo as DamUserInfo | undefined;

        if (userInfo) {
            _damUserInfo = userInfo;
        }
    } catch (err) {
        // eslint-disable-next-line
        console.error('ensureDamUserInfo err: ', err);
    }
};

const getDamAuthHeaders = (): { jwt: string; tenant_id: string } | null => {
    const jwt = getCookie(DAM_ACCESS_TOKEN_COOKIE);
    const tenantId = getCookie(DAM_TENANT_ID_COOKIE);

    if (!jwt || !tenantId) {
        return null;
    }

    return { jwt, tenant_id: tenantId };
};

const damFetchQuery = async (request: RequestParameters, variables: Variables, uploadables?: UploadableMap | null) => {
    try {
        const body = getRequestBody(request, variables, uploadables);

        const doFetch = (damAuthHeaders: { jwt: string; tenant_id: string }) =>
            fetch(GRAPHQL_URL, {
                method: 'POST',
                headers: {
                    ...getHeaders(uploadables),
                    ...damAuthHeaders
                } as unknown as Headers,
                body
            });

        // Read DAM auth from cookies. When they are missing, fetch a fresh session
        // up-front so the first request already carries valid credentials.
        let damAuthHeaders = getDamAuthHeaders();
        let refreshed = false;
        if (!damAuthHeaders) {
            const session = await refreshDamSession();
            damAuthHeaders = { jwt: session.accessToken, tenant_id: session.tenantId };
            refreshed = true;
        }

        const isUnauthenticated = (data: any): boolean =>
            Array.isArray(data?.errors) && data.errors.some((e: any) => e?.extensions?.code === 'UNAUTHENTICATED');

        let fetchRequest = await doFetch(damAuthHeaders);
        let data = await handleData(fetchRequest);

        // Projects returns HTTP 200 with UNAUTHENTICATED in errors when the Zitadel
        // token is expired. Refresh the session once and retry in that case, as well
        // as on HTTP 401.
        if (!refreshed && (fetchRequest.status === 401 || isUnauthenticated(data))) {
            const session = await refreshDamSession();
            damAuthHeaders = { jwt: session.accessToken, tenant_id: session.tenantId };
            fetchRequest = await doFetch(damAuthHeaders);
            data = await handleData(fetchRequest);
            refreshed = true;
        }

        if (fetchRequest.status === 401) {
            throw new Error('Unauthenticated seller');
        }

        if (isMutation(request) && data.errors) {
            throw data;
        }

        if (!data.data) {
            throw data.errors ?? new Error('GraphQL request failed');
        }

        return data ?? {};
    } catch (err) {
        // eslint-disable-next-line
        console.error('damFetchQuery err: ', err);

        throw err;
    }
};

const network = Network.create((request, variables, _cacheConfig, uploadables) => {
    return damFetchQuery(request, variables, uploadables);
}, undefined);

const damEnvironment = new Environment({
    network,
    store: new Store(new RecordSource(), { gcReleaseBufferSize: 10 })
});

export default damEnvironment;
